import { clientOAuth } from '../utils/clientOAuth';
import {
    describeChallengeError,
    PHASE_CREATE,
    PHASE_CHECK,
    KIND_TECHNICAL,
    KIND_WRONG_ANSWER,
    MESSAGES,
} from '../auth/challengeErrors';
import { buildSession, storeSession, MODE_NATIVE_ID, MODE_NATIVE_QR } from '../auth/session';

// Native login modes use the STANDARD challenge sequence of the Keycloak journey (same as the
// plugin): create on /api/v1/client-users/challenges (level and reception mode = the account's),
// verify on /authentification/check-challenge. Both go through the BFF allow-list.
export const CREATE_CHALLENGE_URL = '/back-tenant/api/v1/client-users/challenges';
export const CHECK_CHALLENGE_URL = '/back-tenant/authentification/check-challenge';
export const SESSION_URL = '/back-tenant/api/v1/sessions/';

export const SET_LOGIN_INFORMATION = 'SET_LOGIN_INFORMATION';
export const SET_SESSION_INFORMATION = 'SET_SESSION_INFORMATION';
export const SET_REFUSAL = 'SET_REFUSAL';
export const SET_ATTEMPT_ERROR = 'SET_ATTEMPT_ERROR';
export const SET_SESSION = 'SET_SESSION';
export const SET_SSO_READY = 'SET_SSO_READY';
export const RESET_AUTH = 'RESET_AUTH';

// Outcome of one QR session poll.
export const SESSION_WAITING = 'WAITING';
export const SESSION_PAIRED = 'PAIRED';
export const SESSION_FAILED = 'FAILED';

export const setLoginInformation = (data) => ({ type: SET_LOGIN_INFORMATION, data });
export const setSessionInformation = (data) => ({ type: SET_SESSION_INFORMATION, data });
export const setRefusal = (data) => ({ type: SET_REFUSAL, data });
export const setAttemptError = (data) => ({ type: SET_ATTEMPT_ERROR, data });
// Signed-in state (single source): {mode, displayName}, mirrored in sessionStorage.
export const setSession = (session) => {
    storeSession(session);
    return { type: SET_SESSION, data: buildSession(session.mode, session.displayName) };
};
// The silent Keycloak SSO check has completed (protected routes may redirect from then on).
export const setSsoReady = () => ({ type: SET_SSO_READY });
export const resetAuth = () => ({ type: RESET_AUTH });

const technicalRefusal = () => ({ kind: KIND_TECHNICAL, message: MESSAGES[KIND_TECHNICAL] });

const failure = (error) => {
    const response = error && error.response;
    return { status: response ? response.status : undefined, body: response ? response.data : undefined };
};

export const checkChallenge = (challengeId, userId, challengeResponse) => (dispatch, getState) =>
    clientOAuth()
        .post(CHECK_CHALLENGE_URL, { challengeId, challengeResponse, userId })
        .then(() => {
            const current = getState().authentication.loginInformations || {};
            dispatch(setAttemptError(null));
            dispatch(setSession({ mode: current.mode || MODE_NATIVE_ID, displayName: current.displayName || '' }));
        })
        .catch((error) => {
            const { status, body } = failure(error);
            const outcome = describeChallengeError(PHASE_CHECK, status, body);
            if (outcome.kind === KIND_WRONG_ANSWER) {
                dispatch(setAttemptError(outcome));
            } else {
                dispatch(setRefusal(outcome));
            }
        });

// `mode` and `displayName` describe the sign-in in progress (used once the challenge is solved).
export const validateLogin = (login, mode = MODE_NATIVE_ID, displayName) => (dispatch) => {
    // Sent as typed (trimmed only): no domain is ever appended to the login.
    const typedLogin = typeof login === 'string' ? login.trim() : '';
    dispatch(setRefusal(null));
    dispatch(setAttemptError(null));
    return clientOAuth()
        .post(CREATE_CHALLENGE_URL, { login: typedLogin })
        .then(({ data }) => {
            if (!data || !data.challengeId) {
                dispatch(setRefusal(technicalRefusal()));
                return;
            }
            // ChallengeContext: challengeId, userId, countAttemps, timer, isPublicPass. It carries
            // no challenge value: the value travels on the account's own channel (app, SMS, e-mail).
            dispatch(setLoginInformation({
                challengeId: data.challengeId,
                userId: data.userId,
                countAttemps: data.countAttemps,
                timer: data.timer,
                isPublicPass: data.isPublicPass === true,
                mode,
                displayName: displayName || typedLogin,
            }));
            if (data.isPublicPass === true) {
                // Public pass: verify right away with an empty response, no form (same rule as the plugin).
                return dispatch(checkChallenge(data.challengeId, data.userId, ''));
            }
        })
        .catch((error) => {
            const { status, body } = failure(error);
            dispatch(setRefusal(describeChallengeError(PHASE_CREATE, status, body)));
        });
};

// One poll of the QR session: 404 = not paired yet, 200 = paired (then the standard sequence runs
// with the paired login), anything else = technical failure. Resolves to a SESSION_* outcome so the
// caller can stop polling.
export const getSessionDetails = (sessionId) => (dispatch) =>
    clientOAuth()
        .get(SESSION_URL + encodeURIComponent(sessionId))
        .then(({ data }) => {
            if (!data || !data.login) {
                return SESSION_WAITING;
            }
            dispatch(setSessionInformation({ firstName: data.firstName, lastName: data.lastName, login: data.login }));
            const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
            dispatch(validateLogin(data.login, MODE_NATIVE_QR, fullName || data.login));
            return SESSION_PAIRED;
        })
        .catch((error) => {
            const { status } = failure(error);
            if (status === 404) {
                return SESSION_WAITING;
            }
            dispatch(setRefusal(technicalRefusal()));
            return SESSION_FAILED;
        });
